import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { Dialog, DialogTitle, Tooltip } from '@mui/material';

import { setSuccessMessage } from '../../../store/formsState';
import { loadParticipantByPtCode, searchClaimEvents } from '../../../store/participantService';
import { clearKeys } from '../../../utils/objectUtils';
import { hasRole, PRAP, QUALITY, SUPERUSER } from '../../../utils/userRoles';
import Button from '../../formElements/Button';
import SingleSelect from '../../formElements/SingleSelect';
import TextAreaField from '../../formElements/TextAreaField';

import { validate } from './validateStatusCannotClaimEditor';

import form from '../../../commonStyles/formStyles.module.css';
import modal from '../../../commonStyles/modal.module.css';

const _NOTE_CHAR_LIMIT = 750;
const acceptedRoles = [PRAP, QUALITY, SUPERUSER];

const StatusCannotClaimEditor = ({
    isOpen,
    onStatusCannotClaimEditorChange,
    cannotClaimRow,
    onStatusCannotClaimUpdated,
    claimEventStatuses
}) => {
    // HOOKS
    const dispatch = useDispatch();

    // LOCAL STATE
    const [notes, setNotes] = useState('');
    const [statusId, setStatusId] = useState('');
    const [statusName, setStatusName] = useState('');
    const [errors, setErrors] = useState({});
    const [claimEventStatusesTo, setClaimEventStatusesTo] = useState([]);
    const [claimEventStatusesToTrimmed, setClaimEventStatusesToTrimmed] = useState([]);
    const [openTooltip, setOpenTooltip] = React.useState(false);
    const [keys, setKeys] = useState({
        statusId: '0'
    });

    // STORE STATE
    const claimEvent = useSelector((state) => state.entities.participantService.claimEvent);
    const currentParticipant = useSelector(
        (state) => state.entities.participantService.currentParticipant
    );
    const loggedInUser = useSelector((state) => state.entities.userService.loggedInUser);

    // USE EFFECTS
    useEffect(() => {
        if ('ptCode' in cannotClaimRow) dispatch(loadParticipantByPtCode(cannotClaimRow.ptCode));
        setKeys(clearKeys(keys));
    }, [cannotClaimRow]);

    useEffect(() => {
        if (Object.keys(currentParticipant)?.length > 0) {
            dispatch(
                searchClaimEvents({
                    eligibilityGroupIds: [currentParticipant.eligibilityGroupId],
                    claimTypeId: cannotClaimRow.eventTypeId
                })
            );
        }
    }, [currentParticipant]);

    useEffect(() => {
        if (Object.keys(claimEvent)?.length > 0) {
            const eventStatuses = claimEvent.claimTypeStatuses.find(
                (entry) => entry.statusId === cannotClaimRow.statusId
            );

            if (eventStatuses) {
                const eventToStatusIds = eventStatuses.claimTypeToStatusIds;
                setClaimEventStatusesTo(
                    claimEventStatuses.filter((el) =>
                        eventToStatusIds.some((entry) => entry === el.id)
                    )
                );
            }
        }
    }, [claimEvent]);

    useEffect(() => {
        const trimmedStatuses = claimEventStatusesTo.filter(
            (el) =>
                el.name === 'Pre Payment Fail' ||
                el.name === 'Rejected' ||
                el.name === 'Unclaimable'
        );
        setClaimEventStatusesToTrimmed(trimmedStatuses);
    }, [claimEventStatusesTo]);

    // HELPER FNS
    const clearError = (key) => {
        setErrors((prev) => ({ ...prev, [key]: { ...errors[key], error: false } }));
    };

    const clearData = () => {
        setStatusId('');
        setNotes('');
        setKeys(clearKeys(keys));
        setErrors({});
        onStatusCannotClaimEditorChange(false);
    };

    // EVENT HANDLERS
    const onStatusChange = (chosenId) => {
        clearError('statusId');
        setStatusId(chosenId);
        setStatusName(claimEventStatusesToTrimmed.find((el) => el.id === chosenId)?.name || '');
    };

    const onNotesChange = (e) => {
        clearError('notes');
        setNotes(e.target.value);
    };

    const onTooltipClose = () => {
        if (claimEventStatusesToTrimmed.length === 0) setOpenTooltip(false);
    };

    const onTooltipOpen = () => {
        if (claimEventStatusesToTrimmed.length === 0) setOpenTooltip(true);
    };

    const onSubmit = (e) => {
        e.preventDefault();
        const newErrors = validate(statusId, notes);
        setErrors(newErrors);
        if (Object.keys(newErrors).length > 0) return;
        dispatch(setSuccessMessage(`Cannot Claim status and notes added. Please submit to update`));
        onStatusCannotClaimUpdated({
            ...cannotClaimRow,
            cannotClaimStatusId: statusId,
            cannotClaimStatusName: statusName,
            notes
        });
        clearData();
    };

    // RENDER
    return (
        <div>
            <Dialog
                open={isOpen}
                onClose={clearData}
                aria-labelledby="form-dialog-title"
                fullWidth={true}>
                <div className={modal.modalHeader}>
                    <DialogTitle id="form-dialog-title">Claim Status Cannot Claim</DialogTitle>
                </div>
                <div className={modal.modalContent}>
                    <form onSubmit={onSubmit} className={form.formWrapper} data-testid="form_start">
                        <Tooltip
                            open={openTooltip}
                            onClose={onTooltipClose}
                            onOpen={onTooltipOpen}
                            title="The Participant's Eligibility Group has no required not claimable Claim Statuses for this Claim Type"
                            placement="left-start">
                            <SingleSelect
                                id={'statusId'}
                                key={keys.statusId}
                                label={'Claim Event Status'}
                                placeholder="Select claim event status..."
                                mandatory={claimEventStatusesToTrimmed.length !== 0 || false}
                                disabled={
                                    !hasRole(acceptedRoles, loggedInUser.roles) ||
                                    claimEventStatusesToTrimmed.length === 0 ||
                                    false
                                }
                                menuItems={claimEventStatusesToTrimmed || []}
                                selectedId={statusId || ''}
                                selected={
                                    claimEventStatusesToTrimmed.find((el) => el.id === statusId) ||
                                    {}
                                }
                                error={errors.statusId}
                                onChange={(chosenId) => onStatusChange(chosenId)}
                            />{' '}
                        </Tooltip>

                        <TextAreaField
                            id="notes"
                            label="Cannot Claim Notes"
                            placeholder={'Cannot claim notes'}
                            disabled={!hasRole(acceptedRoles, loggedInUser.roles)}
                            mandatory={true}
                            maxLength={_NOTE_CHAR_LIMIT}
                            value={notes || ''}
                            count={`${notes.length}/${_NOTE_CHAR_LIMIT}`}
                            error={errors.notes}
                            onChange={onNotesChange}
                        />

                        <div className={modal.actionButtons}>
                            <Button
                                id="statusEditorUpdateButton"
                                disabled={
                                    !hasRole(acceptedRoles, loggedInUser.roles) ||
                                    claimEventStatusesToTrimmed.length === 0 ||
                                    false
                                }
                                content="Update"
                            />
                            <Button
                                id="cannotClaimStatusEditorCancelButton"
                                type="button"
                                content="Cancel"
                                onClick={clearData}
                            />
                        </div>
                    </form>
                </div>
            </Dialog>
        </div>
    );
};

StatusCannotClaimEditor.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onStatusCannotClaimEditorChange: PropTypes.func,
    cannotClaimRow: PropTypes.object,
    onStatusCannotClaimUpdated: PropTypes.func,
    claimEventStatuses: PropTypes.array
};

export default StatusCannotClaimEditor;
